import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { Input } from 'semantic-ui-react'
import Script from 'react-load-script'
import { get, isEmpty, trim, isEqual } from 'lodash'
import logger from '@adc/logger'
import classnames from 'classnames/bind'
import { commonPropTypes, formFieldWrapper } from '../formFieldWrapper'
import {
  ADDRESS_FIELD_LABEL,
  GOOGLE_ADDRESS_PARTS,
  MAP_GOOGLE_PROPS_TO_FORM_PROPS,
  STREET_NAME_NUMBER_REGEX,
} from './constants'
import {
  getAddressMapFromPlace,
  mapPropsFromSourceWithMap,
} from './utils/index'

/*
 * AddressSuggestionField has a dependency on google maps places api
 * This component will always return onChange a street_name and street_number as well as the selectedAddressText
 * The street_name and street_number are regexed if the user free formed the input
 * Otherwise those values will be populated from the google autocomplete selection
 * The value returned from this component are suggestions to fill in the other address fields
 *
 * Refer to for api responses and usage:
 * https://developers.google.com/maps/documentation/javascript/places-autocomplete
 * If passing in a value see componentDidMount
 */
export class AddressSuggestionField extends Component {
  static propTypes = {
    compact: PropTypes.bool,
  }

  static defaultProps = {
    compact: false,
  }

  addressInputRef = null

  state = {
    autocomplete: {},
    isMounted: false,
    selectedAddressText: '',
    selectedAddress: {},
  }

  loadScriptError = () => {
    logger.warn('Could not load Google Places Api.')
  }

  /*
   * google maps has succesfully loaded
   */
  loadScriptSuccess = () => {
    // Semantic Input has an inputRef
    const addressInput = this.addressInputRef
    const options = this.props.googleAutocompleteOptions || {}
    const autocomplete = new window.google.maps.places.Autocomplete(
      addressInput.current,
      {
        types: ['address'],
        ...options,
      }
    )

    // When the user selects an address from the dropdown, populate the address state.
    autocomplete &&
      autocomplete.addListener('place_changed', this.addressOptionSelected)
    this.setState({ autocomplete })
  }

  /*
   * The user has selected an option from the Google Maps Api results
   * Save the necessary props to a state object and call parent callback
   */
  addressOptionSelected = () => {
    const {
      googleAddressParts,
      mapGooglePropsToFormProps,
      displayFormattedAddress,
    } = this.props
    const onChange = get(this.props, 'onChange', null)
    // Get the place details from the autocomplete object.
    const place = this.state.autocomplete.getPlace()
    const formattedAddress = place.formatted_address

    // get the address parts
    let selectedAddress = getAddressMapFromPlace(place, googleAddressParts)
    // map selectedAddress
    selectedAddress = mapPropsFromSourceWithMap(
      selectedAddress,
      mapGooglePropsToFormProps
    )
    const builtSelectedAddressText = `${selectedAddress.street_number || ''} ${
      selectedAddress.street_name || ''
    }`
    // the string the user sees for the text input
    const selectedAddressText = displayFormattedAddress
      ? formattedAddress
      : builtSelectedAddressText
    // make sure we have street_name and street_number
    if (
      !selectedAddress[mapGooglePropsToFormProps.route] ||
      !selectedAddress[mapGooglePropsToFormProps.street_number]
    ) {
      selectedAddress = Object.assign(
        selectedAddress,
        this.streetNameNumberRegex(builtSelectedAddressText)
      )
    }
    // add a flag to indicate this is an option selected from the Google Maps Api results
    Object.assign(selectedAddress, { is_option_selected: true })

    this.setState({ selectedAddress, selectedAddressText })
    // fire onChange here since this is the Api value we want sent to the parent
    onChange &&
      onChange(Object.assign({ selectedAddressText }, selectedAddress))
  }

  /**
   * Returns an object with street_name and street_number filled in appropriated based on regex
   * @params inputText String the text in the input the user interacts with
   */
  streetNameNumberRegex = inputText => {
    const { mapGooglePropsToFormProps } = this.props
    const streetNameKey = mapGooglePropsToFormProps.route
    const streetNumberKey = mapGooglePropsToFormProps.street_number
    const selectedAddress = {}
    // clear the selectedAddress since user made an input change
    // attempt to parse the street_name and street_number
    const matches = STREET_NAME_NUMBER_REGEX.exec(inputText || '')
    if (matches) {
      ;[, selectedAddress[streetNumberKey], selectedAddress[streetNameKey]] =
        matches
    } else {
      selectedAddress[streetNumberKey] = ''
      selectedAddress[streetNameKey] = trim(inputText)
    }
    return selectedAddress
  }

  /*
   * Managed Component needs to track the user input and update the value so autocomplete API can hook into
   */
  onChange = (_, data) => {
    const onChange = get(this.props, 'onChange', null)
    const selectedAddressText = data.value

    // clear the selectedAddress since user made an input change
    // attempt to parse the street_name and street_number
    const selectedAddress = this.streetNameNumberRegex(selectedAddressText)
    this.setState({ selectedAddressText, selectedAddress })
    // input field is free form
    onChange &&
      onChange(Object.assign({ selectedAddressText }, selectedAddress))
  }

  onBlur = (_, data) => {
    const { selectedAddressText, selectedAddress } = this.state
    // input may not be defined depending on usage
    const onBlur = get(this.props, 'onBlur', null)
    onBlur && onBlur(Object.assign({ selectedAddressText }, selectedAddress))
  }

  /*
   * Store internal ref to the actual input needed for the autocomplete Api
   */
  addressInput = input => {
    this.addressInputRef = input && input.inputRef
  }

  componentWillUnmount() {
    this.addressInputRef = null
  }

  updateState(value) {
    let nextState = {
      selectedAddress: {},
      selectedAddressText: '',
    }

    if (!isEmpty(value)) {
      // ensure the street_number and street_name are filled in
      const selectedAddressText = value.selectedAddressText || ''
      const selectedAddress = Object.assign(
        this.streetNameNumberRegex(selectedAddressText),
        value
      )
      nextState = {
        selectedAddress,
        selectedAddressText,
      }
    }

    this.setState(nextState)
  }

  /*
   * allows for resetting field
   */
  // eslint-disable-next-line
  UNSAFE_componentWillReceiveProps(nextProps) {
    if (!isEqual(nextProps.value, this.props.value)) {
      this.updateState(nextProps.value)
    }
  }

  /*
   * Use componentDidMount to determine if a value was sent in for AddressField.
   * This normally is done when in edit mode in a form.
   */
  componentDidMount() {
    this.setState({ isMounted: true })
    this.updateState(this.props.value)
  }

  render() {
    const {
      elmId,
      placeholder,
      googleApiConfig: { channelName, clientId },
      loading, // eslint-disable-line no-unused-vars
      googleAddressParts, // eslint-disable-line no-unused-vars
      mapGooglePropsToFormProps, // eslint-disable-line no-unused-vars
      label, // eslint-disable-line no-unused-vars
      onBlur, // eslint-disable-line no-unused-vars
      onChange, // eslint-disable-line no-unused-vars
      onFocus, // eslint-disable-line no-unused-vars
      value, // eslint-disable-line no-unused-vars,
      compact,
      className,
      ...rest
    } = this.props

    const { isMounted, selectedAddressText } = this.state
    const newClassName = classnames({
      compact,
      [className]: true,
    })

    return (
      <div>
        <Input
          autoComplete='new-address'
          className={newClassName}
          ref={this.addressInput}
          placeholder={placeholder || ADDRESS_FIELD_LABEL}
          data-elm-id={`${elmId}_input`}
          onChange={this.onChange}
          onBlur={this.onBlur}
          value={selectedAddressText}
          {...rest}
        />
        {isMounted && (
          <Script
            url={`https://maps.googleapis.com/maps/api/js?language=en&client=${clientId}&libraries=places&channel=${channelName}`}
            onError={this.loadScriptError}
            onLoad={this.loadScriptSuccess}
          />
        )}
      </div>
    )
  }
}

AddressSuggestionField.propTypes = {
  googleApiConfig: PropTypes.shape({
    channelName: PropTypes.string.isRequired,
    clientId: PropTypes.string.isRequired,
  }),
  googleAutocompleteOptions: PropTypes.object,
  displayFormattedAddress: PropTypes.bool,
  mapGooglePropsToFormProps: PropTypes.object,
  ...commonPropTypes,
}

// Look at constants file for samples of how these should be structured
AddressSuggestionField.defaultProps = {
  googleAddressParts: GOOGLE_ADDRESS_PARTS,
  mapGooglePropsToFormProps: MAP_GOOGLE_PROPS_TO_FORM_PROPS,
}

/*
 * Wrap the AddressSuggestionField component so that it plays well with redux-form
 */
const AddressSuggestionFieldWrapped = formFieldWrapper(
  (fieldProps, showError) => {
    const { elmId, placeholder, ...rest } = fieldProps

    return (
      <AddressSuggestionField
        placeholder={placeholder}
        error={showError}
        elmId={elmId}
        {...rest}
      />
    )
  }
)

export default AddressSuggestionFieldWrapped
// A parent app can use these to transform data
export { mapPropsFromSourceWithMap, MAP_GOOGLE_PROPS_TO_FORM_PROPS }
